import type { FC, ReactElement } from "react"

import eq from "lodash-es/eq"
import isEmpty from "lodash-es/isEmpty"
import map from "lodash-es/map"

import { ETypographySize, ETypographyTag, Typography } from "@/3514/components"
import { type IUseTranslation, useTranslation } from "@/hooks"
import type { TEmptyRenderCallback } from "@/shared/types/functions"

import { useCoachingChatContext as useChatContext } from "../context"
import { coachingChatComponentsGuards as guards } from "../guards"
import {
    useCoachingChatConversationStatusSelector as useConversationStatusSelector,
    useCoachingChatFormSelector as useFormSelector
} from "../store"
import { coachingChatStyles as chatStyles } from "../styles"
import {
    ECoachingChatConversationStatus as EConversationStatus,
    ECoachingChatEntityMode as EEntityMode,
    type TCoachingChatSeedQuestionFormField as TFormField
} from "../types"

import { CoachingChatQuestion as Field } from "./coaching-chat.question.component"

const { withModeQuestions: withGuard } = guards

const {
    form: { seedQuestions: listClasses }
} = chatStyles

type TProps = {
    renderGuideModalOpener: TEmptyRenderCallback
}

const List: FC<TProps> = ({ renderGuideModalOpener }: TProps): ReactElement => {
    const { t }: IUseTranslation = useTranslation()

    const {
        entityId,
        entityMode,
        form: { title: chatTitle, ref },
        insights: { isEnabled: areInsightsEnabled }
    }: ReturnType<typeof useChatContext> = useChatContext()

    const { questions }: ReturnType<typeof useFormSelector> = useFormSelector(entityId)

    const chatStatus: EConversationStatus = useConversationStatusSelector(entityId)

    return (
        <div className={listClasses.container({ areInsightsEnabled })} ref={ref}>
            {Boolean(
                !eq(entityMode, EEntityMode.Light) &&
                    (!eq(chatStatus, EConversationStatus.NotAvailable) || areInsightsEnabled)
            ) && (
                <div className={listClasses.heading()}>
                    <Typography
                        className={listClasses.title()}
                        tag={ETypographyTag.Heading3}
                        size={ETypographySize.Heading2}
                        text={
                            !isEmpty(chatTitle) && eq(chatStatus, EConversationStatus.NotAvailable)
                                ? chatTitle
                                : t("entities.coachingChat.chat.title")
                        }
                    />

                    {renderGuideModalOpener()}
                </div>
            )}

            <div className={listClasses.questionsWrapper()}>
                {map<TFormField, ReactElement>(
                    questions,
                    ({ id }: TFormField, idx: number): ReactElement => <Field key={id} index={idx} />
                ) ?? null}
            </div>
        </div>
    )
}

const GuardedList: FC = withGuard<TProps>(List)

GuardedList.displayName = "CoachingChatQuestions"

export { GuardedList as CoachingChatQuestions }
